import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eraser, PenLine } from "lucide-react";

/**
 * Composant canvas de signature numérique.
 *
 * Props:
 *   value    : data URL base64 (string) — signature actuelle (contrôlée par parent)
 *   onChange : (dataUrl|null) => void — appelé quand l'utilisateur termine un trait ou efface
 *   label    : libellé de la zone
 *   height   : hauteur du canvas en px (default 120)
 */
export const SignatureCanvas = ({ value, onChange, label = "Signature", height = 120 }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasInk, setHasInk] = useState(Boolean(value));

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#1e293b";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    setHasInk(true);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <PenLine className="h-4 w-4 text-indigo-600" />
          {label}
        </Label>
        <Button
          size="sm"
          variant="ghost"
          type="button"
          onClick={clear}
          disabled={!hasInk}
          data-testid="signature-clear"
        >
          <Eraser className="h-3.5 w-3.5 mr-1" />
          Effacer
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={height}
        className="w-full border border-dashed border-slate-300 rounded-md bg-white touch-none cursor-crosshair"
        style={{ height: `${height}px` }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <p className="text-xs text-muted-foreground">
        Signez dans la zone ci-dessus — cette signature est enregistrée avec votre fiche
        pour valeur de reconnaissance des heures déclarées.
      </p>
    </div>
  );
};

export default SignatureCanvas;
